import { message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getAddressPayload,
  getOption,
} from '../CreateNewWorkScheduleByPeriod.helpers';
import { useWorkScheduleAddressDirectoryQuery } from '../../../api/useWorkScheduleAddressDirectoryQuery';
import { useWorkScheduleCitiesQuery } from '../../../api/useWorkScheduleCitiesQuery';

const getSelectionKey = (values = []) => values.map(String).sort().join('|');

const getOptionText = (option) =>
  option?.text || option?.content || option?.value;

const getHouseName = (house = {}) =>
  house.name ||
  house.fullName ||
  house.houseName ||
  house.building ||
  house.number;

const getHouseStreetId = (house = {}, selectedStreetIds = []) =>
  house.streetId ??
  house.street?.id ??
  house.street?.streetId ??
  (selectedStreetIds.length === 1 ? selectedStreetIds[0] : undefined);

const getHouseStreetName = (house = {}, streetOption) =>
  house.streetName ||
  house.street?.name ||
  house.street?.fullName ||
  getOptionText(streetOption);

const getUniqueOptions = (options = []) => {
  const optionValueSet = new Set();

  return options.filter((option) => {
    const optionKey = String(option.value);

    if (optionValueSet.has(optionKey)) {
      return false;
    }

    optionValueSet.add(optionKey);
    return true;
  });
};

const getCanonicalHouseOptions = ({
  houses = [],
  initialHouseOptions = [],
  streetIds = [],
  streetOptions = [],
}) => {
  const streetOptionsMap = new Map(
    streetOptions.map((streetOption) => [
      String(streetOption.value),
      streetOption,
    ])
  );
  const directoryHouseOptions = houses.map((house) => {
    const streetId = getHouseStreetId(house, streetIds);
    const streetOption = streetOptionsMap.get(String(streetId));

    return {
      ...getOption({
        id: house.id ?? house.houseId,
        name: getHouseName(house),
      }),
      streetId,
      streetName: getHouseStreetName(house, streetOption),
      cityRegionId: house.cityRegionId ?? house.regionId,
      cityRegionIds: Array.isArray(house.cityRegionIds)
        ? house.cityRegionIds
        : [house.cityRegionId ?? house.regionId].filter(
            (cityRegionId) =>
              cityRegionId !== undefined && cityRegionId !== null
          ),
    };
  });

  return getUniqueOptions([...directoryHouseOptions, ...initialHouseOptions]);
};

const getCityRegionFilteredHouseOptions = ({
  cityRegionIds = [],
  enabled = true,
  houseOptions = [],
}) => {
  const selectedCityRegionIdSet = new Set(cityRegionIds.map(String));

  if (!enabled || !selectedCityRegionIdSet.size) {
    return houseOptions;
  }

  return houseOptions.map((houseOption) => {
    const houseCityRegionIds = Array.isArray(houseOption.cityRegionIds)
      ? houseOption.cityRegionIds
      : [houseOption.cityRegionId ?? houseOption.regionId].filter(
          (cityRegionId) => cityRegionId !== undefined && cityRegionId !== null
        );
    const belongsToSelectedCityRegion = houseCityRegionIds.some(
      (cityRegionId) => selectedCityRegionIdSet.has(String(cityRegionId))
    );

    return {
      ...houseOption,
      disabled: Boolean(houseOption.disabled || belongsToSelectedCityRegion),
    };
  });
};

export const useAddressBlock = ({
  companyId,
  form,
  initialValues = {},
  onDirtyChange,
}) => {
  const { getFieldValue, setFieldsValue } = form;
  const [allCompanyAddresses, setAllCompanyAddresses] = useState(
    Boolean(initialValues.allCompanyAddresses)
  );

  const cityIds = getFieldValue('cityIds') || initialValues.cityIds || [];
  const cityRegionIds =
    getFieldValue('cityRegionIds') || initialValues.cityRegionIds || [];
  const streetIds = getFieldValue('streetIds') || initialValues.streetIds || [];
  const houseIds = getFieldValue('houseIds') || initialValues.houseIds || [];
  const cityIdsKey = getSelectionKey(cityIds);
  const streetIdsKey = getSelectionKey(streetIds);
  const initialCityIdsKey = getSelectionKey(initialValues.cityIds || []);
  const initialStreetIdsKey = getSelectionKey(initialValues.streetIds || []);
  const previousCompanyIdRef = useRef(companyId);
  const previousCityIdsKeyRef = useRef(initialCityIdsKey);
  const previousStreetIdsKeyRef = useRef(initialStreetIdsKey);
  const previousStreetIdsRef = useRef(initialValues.streetIds || []);
  const houseStreetIdByHouseIdRef = useRef(new Map());
  const cityDisabled = allCompanyAddresses || !companyId;
  const cityRegionDisabled =
    allCompanyAddresses || !companyId || cityIds.length === 0;
  const streetDisabled = cityRegionDisabled;
  const houseDisabled =
    streetDisabled || (!cityRegionIds.length && streetIds.length === 0);

  const cityDisabledHint = !companyId
    ? 'Выберите компанию'
    : allCompanyAddresses
    ? 'Включен режим "Все адреса УК"'
    : undefined;
  const streetDisabledHint = !companyId
    ? 'Выберите компанию'
    : allCompanyAddresses
    ? 'Включен режим "Все адреса УК"'
    : cityIds.length === 0
    ? 'Сначала выберите город'
    : undefined;
  const houseDisabledHint = !companyId
    ? 'Выберите компанию'
    : allCompanyAddresses
    ? 'Включен режим "Все адреса УК"'
    : cityIds.length === 0
    ? 'Сначала выберите город'
    : !cityRegionIds.length && streetIds.length === 0
    ? 'Сначала выберите улицу'
    : undefined;
  const cityRegionDisabledHint = !companyId
    ? 'Выберите компанию'
    : allCompanyAddresses
    ? 'Включен режим "Все адреса УК"'
    : cityIds.length === 0
    ? 'Сначала выберите город'
    : undefined;

  useEffect(() => {
    setAllCompanyAddresses(Boolean(initialValues.allCompanyAddresses));
  }, [initialValues.allCompanyAddresses]);

  const citiesQuery = useWorkScheduleCitiesQuery({ companyId });
  const addressDirectoryQuery = useWorkScheduleAddressDirectoryQuery(
    { companyId, cityId: cityIds[0] },
    {
      enabled: !cityRegionDisabled,
      onError: () =>
        message.error('Не удалось загрузить микрорайоны, улицы и дома'),
    }
  );
  const addressDirectory = addressDirectoryQuery.data || {};

  const cityOptions = useMemo(() => {
    const cities = Array.isArray(citiesQuery.data) ? citiesQuery.data : [];

    return getUniqueOptions([
      ...(initialValues.initialCityOptions || []),
      ...cities.map((city) =>
        getOption({
          id: city.id ?? city.cityId,
          name: city.name ?? city.fullName ?? city.cityName,
        })
      ),
    ]);
  }, [citiesQuery.data, initialValues.initialCityOptions]);
  const streetOptions = useMemo(() => {
    const streets = addressDirectory.streets || [];

    return getUniqueOptions([
      ...(initialValues.initialStreetOptions || []),
      ...streets.map((street) =>
        getOption({
          id: street.id ?? street.streetId,
          name:
            street.nameWithPrefix ||
            street.name ||
            street.fullName ||
            street.description ||
            street.streetName,
        })
      ),
    ]);
  }, [addressDirectory.streets, initialValues.initialStreetOptions]);
  const cityRegionOptions = useMemo(() => {
    const cityRegions = Array.isArray(addressDirectory.cityRegions)
      ? addressDirectory.cityRegions
      : [];

    return getUniqueOptions([
      ...(initialValues.initialCityRegionOptions || []),
      ...cityRegions.map((cityRegion) =>
        getOption({
          id: cityRegion.id ?? cityRegion.cityRegionId,
          name: cityRegion.name ?? cityRegion.cityRegionName,
        })
      ),
    ]);
  }, [addressDirectory.cityRegions, initialValues.initialCityRegionOptions]);
  const houseOptions = useMemo(() => {
    const selectedStreetIdSet = new Set(streetIds.map(String));
    const houses = (addressDirectory.houses || []).filter(
      (house) =>
        (!selectedStreetIdSet.size && cityRegionIds.length > 0) ||
        selectedStreetIdSet.has(String(house.streetId))
    );

    const canonicalHouseOptions = getCanonicalHouseOptions({
      houses,
      initialHouseOptions: initialValues.initialHouseOptions,
      streetIds,
      streetOptions,
    });

    return getCityRegionFilteredHouseOptions({
      cityRegionIds,
      enabled: Boolean(addressDirectoryQuery.data),
      houseOptions: canonicalHouseOptions,
    });
  }, [
    addressDirectory.houses,
    addressDirectoryQuery.data,
    cityRegionIds,
    initialValues.initialHouseOptions,
    streetIds,
    streetOptions,
  ]);
  useEffect(() => {
    const disabledHouseIdSet = new Set(
      houseOptions
        .filter((houseOption) => houseOption.disabled)
        .map((houseOption) => String(houseOption.value))
    );

    if (!disabledHouseIdSet.size) {
      return;
    }

    const currentHouseIds = getFieldValue('houseIds') || [];
    const nextHouseIds = currentHouseIds.filter(
      (houseId) => !disabledHouseIdSet.has(String(houseId))
    );

    if (nextHouseIds.length !== currentHouseIds.length) {
      setFieldsValue({ houseIds: nextHouseIds });
    }
  }, [getFieldValue, houseOptions, setFieldsValue]);

  useEffect(() => {
    if (previousCompanyIdRef.current === companyId) {
      return;
    }

    previousCompanyIdRef.current = companyId;
    setFieldsValue({
      cityIds: [],
      cityRegionIds: [],
      streetIds: [],
      houseIds: [],
    });
  }, [companyId, setFieldsValue]);

  useEffect(() => {
    if (previousCityIdsKeyRef.current === cityIdsKey) {
      return;
    }

    previousCityIdsKeyRef.current = cityIdsKey;
    setFieldsValue({
      cityRegionIds: [],
      streetIds: [],
      houseIds: [],
    });
  }, [cityIdsKey, setFieldsValue]);

  useEffect(() => {
    if (previousStreetIdsKeyRef.current === streetIdsKey) {
      return;
    }

    const previousStreetIds = previousStreetIdsRef.current || [];
    const currentStreetIdSet = new Set(streetIds.map(String));
    const removedStreetIdSet = new Set(
      previousStreetIds
        .filter((streetId) => !currentStreetIdSet.has(String(streetId)))
        .map(String)
    );

    previousStreetIdsKeyRef.current = streetIdsKey;
    previousStreetIdsRef.current = streetIds;

    if (!removedStreetIdSet.size) {
      return;
    }

    const currentHouseIds = getFieldValue('houseIds') || [];
    const houseStreetIdByHouseId = houseStreetIdByHouseIdRef.current;
    const nextHouseIds = currentHouseIds.filter((houseId) => {
      const streetId = houseStreetIdByHouseId.get(String(houseId));

      if (streetId === undefined || streetId === null) {
        return true;
      }

      return !removedStreetIdSet.has(String(streetId));
    });

    if (nextHouseIds.length !== currentHouseIds.length) {
      setFieldsValue({
        houseIds: nextHouseIds,
      });
    }
  }, [getFieldValue, setFieldsValue, streetIds, streetIdsKey]);

  useEffect(() => {
    const houseStreetIdByHouseId = new Map(houseStreetIdByHouseIdRef.current);

    houseOptions.forEach((houseOption) => {
      if (
        houseOption.value === undefined ||
        houseOption.value === null ||
        houseOption.streetId === undefined ||
        houseOption.streetId === null
      ) {
        return;
      }

      houseStreetIdByHouseId.set(
        String(houseOption.value),
        houseOption.streetId
      );
    });

    houseStreetIdByHouseIdRef.current = houseStreetIdByHouseId;
  }, [houseOptions]);

  useEffect(() => {
    if (
      allCompanyAddresses ||
      cityOptions.length !== 1 ||
      streetIds.length ||
      houseIds.length
    ) {
      return;
    }

    const currentCityIds = getFieldValue('cityIds') || [];
    const [cityOption] = cityOptions;

    if (currentCityIds.length !== 1 || currentCityIds[0] !== cityOption.value) {
      setFieldsValue({ cityIds: [cityOption.value] });
    }
  }, [
    allCompanyAddresses,
    cityOptions,
    getFieldValue,
    houseIds.length,
    setFieldsValue,
    streetIds.length,
  ]);

  const handleAllCompanyAddressesChange = (checked) => {
    setAllCompanyAddresses(checked);
    if (typeof onDirtyChange === 'function') {
      onDirtyChange(checked);
    }
    if (checked) {
      setFieldsValue({
        cityIds: [],
        cityRegionIds: [],
        streetIds: [],
        houseIds: [],
      });
    }
  };

  const getPayload = (values) =>
    getAddressPayload({
      companyId,
      allCompanyAddresses,
      cityId:
        initialValues.cityId ??
        citiesQuery.data?.[0]?.id ??
        citiesQuery.data?.[0]?.cityId,
      cityIds: values.cityIds || [],
      cityRegionIds: values.cityRegionIds || [],
      streetIds: values.streetIds || [],
      houseIds: values.houseIds || [],
      streetOptions,
      houseOptions,
    });

  return {
    allCompanyAddresses,
    cityOptions,
    cityRegionOptions,
    cityRegionDisabled,
    cityRegionDisabledHint,
    cityDisabled,
    cityDisabledHint,
    streetIds,
    streetOptions,
    streetDisabled,
    streetDisabledHint,
    houseOptions,
    houseDisabled,
    houseDisabledHint,
    citiesLoading: citiesQuery.isFetching,
    cityRegionsLoading: addressDirectoryQuery.isFetching,
    streetsLoading: addressDirectoryQuery.isFetching,
    housesLoading: addressDirectoryQuery.isFetching,
    onAllCompanyAddressesChange: handleAllCompanyAddressesChange,
    getPayload,
  };
};
